import MarkdownExplanation from "./MarkdownExplanation";
import type { GuidedQuestionHistoryEntry } from "../services/guidedSessionService";

interface Props {
  entries: GuidedQuestionHistoryEntry[];
}

// Purely presentational — data in, cards out. Used both by the live
// conclude screen (UnderstandSessionPage.tsx) and the history detail page,
// so a future per-entry action (e.g. "Practice again") can be added later
// without restructuring how this renders.
export default function GuidedSessionBreakdown({ entries }: Props) {
  if (entries.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Here's how each question went</h3>
      <div className="space-y-4">
        {entries.map((entry, i) => (
          <div key={i} className="rounded-2xl border border-gray-200 bg-white/70 backdrop-blur-md shadow-lg p-4">
            <div className="flex items-start justify-between gap-2 mb-2">
              <MarkdownExplanation text={entry.question} variant="light" className="font-medium text-gray-900 flex-1" />
              <span
                className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${
                  entry.ui_status === "solid"
                    ? "bg-emerald-100 text-emerald-700"
                    : entry.ui_status === "mostly_there"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-orange-100 text-orange-700"
                }`}
              >
                {entry.ui_status === "solid" ? "Solid" : entry.ui_status === "mostly_there" ? "Mostly there" : "Needs review"}
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-2">{entry.ui_message}</p>
            {entry.question_type === "mcq" && (
              <p className="text-sm text-gray-800 mb-1">
                <span className="font-semibold">Your answer:</span>{" "}
                {entry.student_answer != null && entry.options
                  ? Object.values(entry.options)[entry.student_answer as number]
                  : "Skipped"}
              </p>
            )}
            {entry.correct_answer && (
              <p className="text-sm text-gray-800 mb-2">
                <span className="font-semibold">Correct answer:</span> {entry.correct_answer}
              </p>
            )}
            {entry.solution_steps.length > 0 && (
              <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1">
                {entry.solution_steps.map((step, si) => (
                  <li key={si}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
